/**
 * Single source of truth for every stable, module-namespaced error code.
 *
 * ERROR_CODES.md is generated from this registry (`pnpm errors:docs`) and
 * verified in CI/tests (`pnpm errors:check`). Never reuse a code for a
 * different error; retire codes by setting `status: 'retired'` instead of deleting.
 *
 * Ordinary child mistakes in a game are gameplay feedback, NOT error codes.
 */

export type ErrorSeverity = 'WARN' | 'ERROR' | 'CRITICAL';

/** `active` = emitted by implemented code; `reserved` = allocated for a later phase, not yet emitted. */
export type ErrorCodeStatus = 'active' | 'reserved' | 'retired';

export interface ErrorCodeDefinition {
  readonly code: string;
  readonly title: string;
  readonly meaning: string;
  readonly likelyCauses: readonly string[];
  /** package/file/function that raises it (or the planned module for reserved codes). */
  readonly origin: string;
  readonly severity: ErrorSeverity;
  /** Safe to show to a child or tester; must name a recovery action. The code is appended by the UI. */
  readonly userMessage: string;
  readonly troubleshooting: readonly string[];
  /** Phase in which the code became / becomes active. */
  readonly phase: number;
  readonly status: ErrorCodeStatus;
}

export const ERROR_CODE_PATTERN = /^[A-Z]+-[A-Z]+-\d{3}$/;

const definitions = [
  // ---------------------------------------------------------------- WEB
  {
    code: 'WEB-NAV-001',
    title: 'Unknown or unavailable route',
    meaning:
      'The browser requested a route or screen state that does not exist or is not available.',
    likelyCauses: [
      'Mistyped or outdated URL',
      'Bookmark to a removed screen',
      'Link to a feature not yet implemented',
    ],
    origin: 'apps/web/src/app/NotFound.tsx (NotFound)',
    severity: 'WARN',
    userMessage: "We couldn't find that page. Go back to Discover to keep exploring.",
    troubleshooting: [
      'Check the URL against routes in apps/web/src/app/routes.tsx',
      'Export browser diagnostics from /diagnostics and look for WEB-NAV-001 entries',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'WEB-RUNTIME-001',
    title: 'Unhandled interface error',
    meaning:
      'A React render error or uncaught browser exception reached the application error boundary.',
    likelyCauses: [
      'Programming defect in a screen component',
      'Unexpected data shape from API or storage',
    ],
    origin:
      'apps/web/src/app/AppErrorBoundary.tsx (componentDidCatch); apps/web/src/diagnostics/global-handlers.ts',
    severity: 'ERROR',
    userMessage:
      'This screen stopped working. Reload the page — your saved projects are kept in this browser.',
    troubleshooting: [
      'Open /diagnostics and export the JSONL log',
      'Find the WEB-RUNTIME-001 entry; its context.component and context.errorName identify the failing component',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'WEB-LOG-001',
    title: 'Browser diagnostics could not be persisted',
    meaning:
      'The local diagnostics buffer in IndexedDB could not be opened or written; entries are kept in memory only for this page session.',
    likelyCauses: [
      'Private browsing mode restricting IndexedDB',
      'Storage quota exceeded',
      'Browser storage disabled by policy',
    ],
    origin: 'apps/web/src/diagnostics/browser-logger.ts (BrowserLogger.persist)',
    severity: 'WARN',
    userMessage:
      'Diagnostics are only kept until you close this page. You can still export them from the diagnostics page.',
    troubleshooting: [
      'Check browser storage settings / private mode',
      'Export diagnostics before closing the tab',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'WEB-API-001',
    title: 'API unreachable from browser',
    meaning:
      'A browser request to the EBU Kids World API failed at network level or returned a non-JSON/unknown response.',
    likelyCauses: [
      'API server not running (`pnpm dev`)',
      'Reverse proxy misconfiguration',
      'Network offline',
    ],
    origin: 'apps/web/src/adapters/api-client.ts (apiGet)',
    severity: 'ERROR',
    userMessage:
      "We can't reach the Kids World service right now. Check your connection and try again.",
    troubleshooting: [
      'curl http://localhost:3001/api/health',
      'Check API logs in LOG_DIR (default ./logs/api) for the correlation ID',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'WEB-STORAGE-001',
    title: 'Local project storage unavailable',
    meaning:
      'The IndexedDB project database could not be opened, so projects cannot be saved in this browser.',
    likelyCauses: [
      'Private browsing mode restricting IndexedDB',
      'Browser storage disabled by policy or settings',
      'Another tab holds an older database version open',
    ],
    origin: 'apps/web/src/adapters/local-store/local-store.ts (LocalStore.open)',
    severity: 'WARN',
    userMessage:
      "This browser can't save projects right now. You can still play — export your work to keep it.",
    troubleshooting: [
      'Check IndexedDB availability on /diagnostics',
      'Leave private browsing mode or allow site storage',
      'Close other Kids World tabs and reload',
    ],
    phase: 2,
    status: 'active',
  },
  // ---------------------------------------------------------------- API
  {
    code: 'API-INTERNAL-001',
    title: 'Unhandled API error',
    meaning: 'An exception without a registered code reached the Fastify error handler.',
    likelyCauses: ['Programming defect in a route handler', 'Unexpected dependency failure'],
    origin: 'apps/api/src/plugins/error-handler.ts (errorHandler)',
    severity: 'ERROR',
    userMessage: 'The service had a problem with that request. Please try again shortly.',
    troubleshooting: [
      'Search API JSONL logs for the response requestId',
      'The log entry includes errorName and a sanitised stack origin',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'API-ROUTE-001',
    title: 'Unknown API route',
    meaning: 'A request reached the API for a method/path combination that is not registered.',
    likelyCauses: [
      'Client calling an endpoint from a later phase',
      'Typo in API path',
      'Reverse proxy path rewrite error',
    ],
    origin: 'apps/api/src/app.ts (setNotFoundHandler)',
    severity: 'WARN',
    userMessage: "That service address doesn't exist. Reload the page to get the latest version.",
    troubleshooting: [
      'List registered routes in apps/api/src/modules/*/routes.ts',
      'Check reverse proxy /api prefix handling',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'API-RATE-001',
    title: 'Request rate limit exceeded',
    meaning: 'A client exceeded the configured request rate for a public or diagnostics endpoint.',
    likelyCauses: ['Automated or abusive traffic', 'Client retry loop'],
    origin:
      'apps/api/src/app.ts (rate-limit errorResponseBuilder); counters shared in PostgreSQL by apps/api/src/modules/auth/limits-store.ts (Phase 10)',
    severity: 'WARN',
    userMessage: 'Too many requests. Please wait a moment and try again.',
    troubleshooting: [
      'Check RATE_LIMIT_MAX / RATE_LIMIT_WINDOW_MS in .env',
      'Look for repeated requestIds from one client in logs',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'API-CONFIG-001',
    title: 'Invalid server configuration',
    meaning:
      'Environment variables failed validation at API start-up; the server refuses to start.',
    likelyCauses: ['Missing or malformed variable in .env', '.env not created from .env.example'],
    origin: 'apps/api/src/config.ts (loadConfig)',
    severity: 'CRITICAL',
    userMessage: 'The service is not configured correctly.',
    troubleshooting: [
      'Compare .env with .env.example',
      'The start-up error lists each invalid variable name (values are never printed)',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'API-DB-001',
    title: 'Database connectivity failure',
    meaning:
      'The API or a database script could not connect to PostgreSQL, or the server closed an idle pooled connection (logged as WARN with the PostgreSQL code; the pool reconnects).',
    likelyCauses: [
      'Database container not running (`pnpm db:up`)',
      'Wrong DATABASE_URL',
      'Credentials rejected',
      'Database restarted, or a throwaway test database was dropped (pgCode 57P01)',
    ],
    origin:
      'apps/api/src/db/client.ts (createPool / checkDatabase); apps/api/src/plugins/error-handler.ts (requests while the database is unreachable → 503)',
    severity: 'CRITICAL',
    userMessage: 'The service is temporarily unavailable. Please try again later.',
    troubleshooting: [
      'Run `pnpm db:up` and `docker compose ps`',
      'Verify DATABASE_URL host/port/database in .env',
      'GET /api/health reports database status',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'API-DB-002',
    title: 'Database migration failed',
    meaning:
      'A migration file failed to apply, or an applied migration was modified after being applied (checksum mismatch).',
    likelyCauses: [
      'SQL error in a new migration',
      'Editing a migration that has already been applied',
    ],
    origin: 'apps/api/src/db/migrate.ts (runMigrations)',
    severity: 'CRITICAL',
    userMessage: 'The service is temporarily unavailable. Please try again later.',
    troubleshooting: [
      'Read the migration name in the error',
      'Never edit applied migrations; add a new numbered migration instead',
      'Each migration runs in its own transaction and is rolled back on failure',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'API-LOG-001',
    title: 'Server log file write failed',
    meaning: 'The JSONL file log sink could not write or rotate; entries fall back to stderr.',
    likelyCauses: ['LOG_DIR not writable', 'Disk full', 'Mounted log volume missing'],
    origin: 'apps/api/src/logging/jsonl-file-sink.ts (JsonlFileSink.write)',
    severity: 'ERROR',
    userMessage: 'The service is temporarily unavailable. Please try again later.',
    troubleshooting: [
      'Check permissions and free space of LOG_DIR',
      'See docs/architecture/logging-operations.md',
    ],
    phase: 1,
    status: 'active',
  },
  {
    code: 'API-DATA-001',
    title: 'Stored data violates the shared contract',
    meaning:
      'A database row could not be mapped to its shared Zod contract (e.g. missing English fallback text or an invalid value); the request is refused rather than serving inconsistent data.',
    likelyCauses: [
      'Manual database edit bypassing the editorial API',
      'Seed or migration out of step with packages/contracts',
    ],
    origin: 'apps/api/src/modules/catalogue/repository.ts (loadCatalogueSnapshot)',
    severity: 'ERROR',
    userMessage: 'The catalogue is temporarily unavailable. Please try again later.',
    troubleshooting: [
      'The log context names the table, row id and failing field paths (never values)',
      'Run `pnpm test:db` to validate seeds against the contracts',
    ],
    phase: 2,
    status: 'active',
  },
  // ------------------------------------------------------------- SECURITY
  {
    code: 'SEC-VALIDATION-001',
    title: 'Malformed or untrusted input rejected',
    meaning:
      'Request input failed shared-schema validation or size limits and was rejected without processing.',
    likelyCauses: ['Client/server contract mismatch', 'Oversized or malicious payload'],
    origin:
      'apps/api/src/plugins/error-handler.ts (validation branch); apps/api/src/modules/diagnostics/routes.ts',
    severity: 'WARN',
    userMessage: 'That request could not be accepted. Please check the information and try again.',
    troubleshooting: [
      'The response lists the invalid field paths (never the values)',
      'Compare against the Zod schema in packages/contracts',
    ],
    phase: 1,
    status: 'active',
  },
  // -------------------------------------------------------------- CONTENT
  {
    code: 'CONTENT-RIGHTS-001',
    title: 'Playback unavailable due to rights rule',
    meaning:
      'Playback is blocked because the territory, date window, review state or delivery-method rights do not permit it.',
    likelyCauses: [
      'Content not licensed for the selected country',
      'Rights window expired or not yet started',
      'Only link/embed rights, no streaming right',
    ],
    origin: 'packages/domain/src/rights.ts (assertPlayable)',
    severity: 'WARN',
    userMessage:
      "This programme isn't available in your selected country right now. Try another programme.",
    troubleshooting: [
      'The error context carries the rights decision reason',
      'Inspect rights_grant rows for the item',
      'Check the selected broadcaster country in /diagnostics',
    ],
    phase: 2,
    status: 'active',
  },
  // ---------------------------------------------------------------- WORLD
  {
    code: 'WORLD-SAVE-001',
    title: 'Project failed validation before saving',
    meaning:
      'A world project did not match the current world schema when saving, so nothing was written.',
    likelyCauses: ['Programming defect in the workshop editor producing an invalid scene'],
    origin: 'apps/web/src/adapters/local-store/local-store.ts (LocalStore.saveProject)',
    severity: 'ERROR',
    userMessage:
      "We couldn't save this version of your workshop. Your last saved version is unchanged — try exporting your project.",
    troubleshooting: [
      'The log context lists the failing schema paths',
      'Validate the scene with validateWorld() from @ebu/world-schema',
    ],
    phase: 2,
    status: 'active',
  },
  {
    code: 'WORLD-SAVE-002',
    title: 'Workshop save failed',
    meaning: 'IndexedDB write or quota failure while saving a world project.',
    likelyCauses: ['Storage quota exceeded', 'Private browsing restrictions'],
    origin: 'apps/web/src/adapters/local-store/local-store.ts (LocalStore.saveProject)',
    severity: 'ERROR',
    userMessage: "We couldn't save your workshop. Try exporting your project first.",
    troubleshooting: [
      'context.quotaExceeded tells whether the browser quota was hit',
      'Export the project to JSON',
      'Free browser storage',
    ],
    phase: 2,
    status: 'active',
  },
  {
    code: 'WORLD-LOAD-001',
    title: 'Saved project unreadable',
    meaning:
      'A project stored in this browser is corrupt or does not match any known schema version. It is kept (never silently deleted) and can be exported raw or deleted explicitly.',
    likelyCauses: [
      'Storage corruption or a manual edit in browser developer tools',
      'Project saved by a newer version of Kids World',
    ],
    origin:
      'apps/web/src/adapters/local-store/local-store.ts (LocalStore.loadProject / listProjects)',
    severity: 'WARN',
    userMessage:
      "We couldn't open this saved project. It has been kept — you can download it or delete it.",
    troubleshooting: [
      'Use exportRawRecord to download the stored record for inspection',
      'context.reason gives the parse failure category',
    ],
    phase: 2,
    status: 'active',
  },
  {
    code: 'WORLD-IMPORT-003',
    title: 'Imported world invalid',
    meaning: 'An imported world file failed schema, version, size or object-count validation.',
    likelyCauses: ['File is not a Kids World export', 'File too large or corrupted'],
    origin:
      'packages/world-schema/src/parse.ts (parseWorldFile); apps/web/src/adapters/local-store/local-store.ts (LocalStore.importProject); apps/web/src/features/world-builder/use-projects.ts (importFile, size check)',
    severity: 'WARN',
    userMessage:
      "This file isn't a Kids World project we can open. Your existing projects are unchanged.",
    troubleshooting: [
      'context.reason: too-large | not-json | wrong-format | newer-version | too-many-objects | invalid',
      'Validate the file with parseWorldFile() from @ebu/world-schema',
    ],
    phase: 2,
    status: 'active',
  },
  // --------------------------------------------------- reserved for later phases
  {
    code: 'CONTENT-MEDIA-002',
    title: 'Licensed media playback failed',
    meaning:
      'The HTML5 video element failed to load or decode a rights-cleared asset (or a caption file failed to load).',
    likelyCauses: ['Missing media file', 'Unsupported codec', 'Network error or offline'],
    origin: 'apps/web/src/features/watch/VideoPlayer.tsx (VideoPlayer)',
    severity: 'ERROR',
    userMessage: "This video couldn't play. Try again, or choose a game or the workshop instead.",
    troubleshooting: [
      'Check the media_asset location and mime type (GET /api/public/catalogue shows media.src)',
      'Verify the file exists under apps/web/public/media and matches manifest.json / CONTENT_RIGHTS.md',
      'The error context carries the MediaError code (1 aborted, 2 network, 3 decode, 4 unsupported)',
    ],
    phase: 5,
    status: 'active',
  },
  {
    code: 'EDITOR-AUTH-001',
    title: 'Staff login rejected',
    meaning:
      'Staff authentication failed: wrong credentials, a disabled account, or too many recent failed attempts for the username. The response is deliberately identical for all of these.',
    likelyCauses: ['Wrong username/password', 'Account disabled', 'Too many attempts (HTTP 429)'],
    origin: 'apps/api/src/modules/auth/routes.ts (POST /api/staff/session)',
    severity: 'WARN',
    userMessage: 'Sign-in failed. Check your username and password, or wait a few minutes.',
    troubleshooting: [
      "Look up auth.login-failed rows in audit_event; change_summary.reason is 'bad-credentials', 'disabled' or 'throttled'",
      'An admin can reset the password (PUT /api/staff/users/:id/password) or re-enable the account',
      'If no admin can sign in, run the one-time `pnpm staff:create-admin` with a new username',
    ],
    phase: 3,
    status: 'active',
  },
  {
    code: 'EDITOR-AUTH-002',
    title: 'Staff session missing or expired',
    meaning:
      'A staff endpoint was called without a valid session: no cookie, an unknown/revoked session, idle or absolute expiry, or the account was disabled.',
    likelyCauses: [
      'Not signed in',
      'Idle timeout (STAFF_SESSION_IDLE_MINUTES) or absolute lifetime reached',
      'Signed out, password changed or account disabled elsewhere',
    ],
    origin: 'apps/api/src/modules/auth/guard.ts (requireStaff)',
    severity: 'WARN',
    userMessage: 'Your staff session has ended. Sign in again to continue.',
    troubleshooting: [
      'Sign in again; context.reason in the server log names the cause',
      'Check that the browser accepts the Secure session cookie (use https or localhost)',
    ],
    phase: 3,
    status: 'active',
  },
  {
    code: 'EDITOR-AUTH-003',
    title: 'Staff role not permitted',
    meaning: 'A signed-in staff member attempted an action reserved for another role.',
    likelyCauses: [
      'Editor tried an admin-only action (staff management, rights approval, deletion)',
    ],
    origin: 'apps/api/src/modules/auth/guard.ts (requireStaff / assertRole)',
    severity: 'WARN',
    userMessage: 'Your staff role cannot do this. Ask an administrator.',
    troubleshooting: [
      'Role matrix: docs/decisions/0006-staff-authentication.md',
      'context.required in the server log names the needed role',
    ],
    phase: 3,
    status: 'active',
  },
  {
    code: 'EDITOR-PUBLISH-002',
    title: 'Publishing blocked by rights or localisation',
    meaning:
      'Publishing (or editing a published item) was blocked because rights are missing/expired, required localisation is absent or the linked mission is not published.',
    likelyCauses: [
      'No approved rights_grant',
      'All approved grants have ended',
      'Missing English or origin-language title/description',
      'Linked mission missing or not published',
    ],
    origin: 'apps/api/src/modules/catalogue/editorial-store.ts (assertPublishable)',
    severity: 'WARN',
    userMessage: 'This item cannot be published yet: add valid rights and required translations.',
    troubleshooting: [
      'The response issues list each failed rule (path + problem)',
      'GET /api/staff/content/:id shows the same readiness list before publishing',
    ],
    phase: 3,
    status: 'active',
  },
  {
    code: 'EDITOR-CONFLICT-001',
    title: 'Editorial change based on a stale version',
    meaning:
      'The expectedVersion sent with a change does not match the stored version: someone else changed the record first.',
    likelyCauses: [
      'Two editors editing the same item',
      'Form opened before another change was saved',
    ],
    origin: 'apps/api/src/modules/catalogue/editorial-store.ts (lockVersioned)',
    severity: 'WARN',
    userMessage: 'Someone else changed this item. Reload it, then make your change again.',
    troubleshooting: [
      'Reload the record; its audit trail (GET /api/staff/audit?targetType=...&targetId=...) shows the other change',
    ],
    phase: 3,
    status: 'active',
  },
  {
    code: 'EDITOR-INPUT-001',
    title: 'Editorial change conflicts with existing data',
    meaning:
      'A well-formed editorial change was refused by a data rule: unknown referenced record, duplicate identifier, deleting something still in use, an invalid state transition, or (Phase 9) broadcaster accent colours that are not readable together.',
    likelyCauses: [
      'Linked mission, media asset or broadcaster does not exist',
      'Identifier or series/season/episode already used',
      'Deleting a published item or a mission/media asset still referenced',
      'Accent text on the accent below 4.5:1 contrast (context.problem = low-contrast)',
    ],
    origin:
      'apps/api/src/modules/catalogue/db-errors.ts (mapDbError), editorial-store.ts (updateBranding) and editorial routes',
    severity: 'WARN',
    userMessage: 'This change conflicts with existing content. Check the highlighted fields.',
    troubleshooting: ['The response issues name the field and problem'],
    phase: 3,
    status: 'active',
  },
  {
    code: 'EDITOR-NOTFOUND-001',
    title: 'Editorial record not found',
    meaning:
      'A staff request named a content item, mission, grant, media asset, broadcaster or user that does not exist.',
    likelyCauses: ['Record deleted by another editor', 'Mistyped identifier'],
    origin: 'apps/api/src/modules/catalogue/editorial-store.ts and other editorial routes',
    severity: 'WARN',
    userMessage: 'That record no longer exists. Reload the list.',
    troubleshooting: ['Check the audit trail for a delete event on the identifier'],
    phase: 3,
    status: 'active',
  },
  {
    code: 'EDITOR-STAFF-001',
    title: 'Staff account provisioning refused',
    meaning:
      'Creating a staff account or setting a password was refused: the password policy failed, the username exists, or the one-time admin command is missing its environment variables.',
    likelyCauses: [
      'Password shorter than 12 characters, repetitive or containing the username',
      'Username already taken',
      'STAFF_ADMIN_USERNAME / STAFF_ADMIN_PASSWORD not set for pnpm staff:create-admin',
    ],
    origin: 'apps/api/src/modules/auth/staff-store.ts (createStaffUser, setPassword)',
    severity: 'WARN',
    userMessage: 'The account could not be saved. Choose a longer password or another username.',
    troubleshooting: [
      'context.reason names the failed rule (never the password)',
      'Remove STAFF_ADMIN_PASSWORD from .env after the admin exists',
    ],
    phase: 3,
    status: 'active',
  },
  {
    code: 'SEC-CSRF-001',
    title: 'Cross-site request refused',
    meaning:
      'A state-changing staff request failed the CSRF defence: foreign Origin / Sec-Fetch-Site, or a missing or wrong X-CSRF-Token header.',
    likelyCauses: [
      'Request from another site (attack or misconfigured proxy)',
      'Client did not send the csrfToken from the session response',
      'Session changed in another tab',
    ],
    origin: 'apps/api/src/modules/auth/guard.ts (checkCsrf)',
    severity: 'WARN',
    userMessage: 'This request was blocked for security. Reload the page and try again.',
    troubleshooting: [
      'context.reason: foreign-origin, cross-site-fetch, missing-token or token-mismatch',
      'Behind a proxy, the Host header must match the browser Origin (do not rewrite Host)',
    ],
    phase: 3,
    status: 'active',
  },
  {
    code: 'CONTENT-CONFIG-001',
    title: 'Unknown broadcaster configuration',
    meaning: 'The public catalogue was requested for a broadcaster that does not exist.',
    likelyCauses: ['Outdated broadcaster id in a link or local setting'],
    origin: 'apps/api/src/modules/catalogue/public-routes.ts (GET /api/public/catalogue)',
    severity: 'WARN',
    userMessage: "We couldn't find that broadcaster. Choose another one from the list.",
    troubleshooting: ['GET /api/public/broadcasters lists valid ids'],
    phase: 3,
    status: 'active',
  },
  {
    code: 'CONTENT-CATALOGUE-001',
    title: 'Programme not listed for the selected broadcaster',
    meaning:
      "A programme page was opened for an item that is not in the selected broadcaster's public catalogue (not curated there, hidden by rights, unpublished or unknown).",
    likelyCauses: [
      'Broadcaster switched while a programme page was open',
      'Old or shared link to a retracted, expired or territory-restricted programme',
      'Mistyped programme address',
    ],
    origin: 'apps/web/src/features/catalogue/ProgrammePage.tsx (ProgrammePage)',
    severity: 'WARN',
    userMessage:
      "This programme isn't listed for the broadcaster you chose. Pick another programme from the catalogue.",
    troubleshooting: [
      'GET /api/public/catalogue?broadcaster=<id>&locale=en lists the visible items',
      'Check the item publish state, curation and rights grants in the editorial API',
    ],
    phase: 4,
    status: 'active',
  },
  {
    code: 'GAME-CIRCUIT-001',
    title: 'Circuit graph invalid',
    meaning:
      'A challenge manifest or circuit graph could not be processed (malformed manifest, unknown manifest version, or a board the engine rejects), as opposed to an ordinary wrong answer.',
    likelyCauses: [
      'Invalid or missing challenge manifest',
      'Mission points at a manifest id/version the client does not have',
      'Corrupt saved circuit',
    ],
    origin:
      'apps/web/src/features/discovery-game/game-model.ts (loadManifest); packages/circuit-engine (CircuitGraphError)',
    severity: 'ERROR',
    userMessage: "This challenge couldn't load. Try another challenge.",
    troubleshooting: [
      'Validate the manifest with circuitManifestSchema (packages/contracts/src/game-manifest.ts)',
      'The log context names the challenge and the engine problem (no-battery, unknown-part, …)',
      'Check the mission gameManifestId / manifestSchemaVersion in the editorial API',
    ],
    phase: 6,
    status: 'active',
  },
  {
    code: 'GAME-MISSION-001',
    title: 'Mission not available',
    meaning:
      'A guest opened a game link for a mission that does not exist or is not published (GET /api/public/missions/:id returned 404).',
    likelyCauses: ['Old or mistyped link', 'Mission unpublished or retracted by editors'],
    origin:
      'apps/api/src/modules/missions/public-routes.ts; apps/web/src/features/discovery-game/GamePage.tsx',
    severity: 'WARN',
    userMessage: 'This challenge is not available. Choose another one in the Lab.',
    troubleshooting: [
      'GET /api/public/missions lists the published missions',
      'Check the mission publish state in the editorial API',
    ],
    phase: 6,
    status: 'active',
  },
  {
    code: 'LEARN-EVIDENCE-001',
    title: 'Evidence could not be persisted',
    meaning:
      'A finished challenge attempt or the recomputed skill evidence could not be written to (or read from) this browser’s storage after the child chose to save progress.',
    likelyCauses: [
      'IndexedDB unavailable (private browsing, blocked site data)',
      'Storage quota exceeded',
      'Record failed validation',
    ],
    origin:
      'apps/web/src/adapters/local-store/local-store.ts (saveLearning / listAttempts / deleteLearning); apps/web/src/features/discovery-game/learning-state.tsx',
    severity: 'WARN',
    userMessage: "We couldn't save your progress on this browser. You can keep playing.",
    troubleshooting: [
      'Check browser storage availability in /diagnostics',
      'The log context says whether the quota was exceeded',
      'Progress stays in memory for this visit; nothing is sent to the server',
    ],
    phase: 7,
    status: 'active',
  },
  {
    code: 'WORLD-RENDER-001',
    title: 'WebGL2 unsupported or 3D renderer setup failed',
    meaning:
      'A 3D view (the town entrance from Phase 4, the workshop from Phase 8) could not start; the 2D alternative (simple map / workshop plan) is shown and the rest of the site stays usable.',
    likelyCauses: [
      'WebGL2 disabled or unsupported',
      'GPU driver blocklisted',
      'The 3D engine chunk failed to download',
    ],
    origin:
      'apps/web/src/features/hub/HubPage.tsx (IslandStage); apps/web/src/features/world-builder/WorkshopPage.tsx (Workshop3D onFailure)',
    severity: 'WARN',
    userMessage:
      "Your browser can't show the 3D view. The simple map works the same way, and you can still watch and explore.",
    troubleshooting: [
      'Check renderer capability on /diagnostics',
      'Enable hardware acceleration in the browser settings',
      'The error context names the failure (town: webgl2-missing / engine-load / scene-init; workshop: webgl2-unavailable / chunk-load-failed / scene-setup-failed)',
    ],
    phase: 4,
    status: 'active',
  },
] as const satisfies readonly ErrorCodeDefinition[];

export type ErrorCode = (typeof definitions)[number]['code'];

export const ERROR_CODES: readonly ErrorCodeDefinition[] = definitions;

const byCode = new Map<string, ErrorCodeDefinition>(definitions.map((d) => [d.code, d]));

export function getErrorDefinition(code: ErrorCode): ErrorCodeDefinition {
  const def = byCode.get(code);
  if (!def) throw new Error(`Unregistered error code: ${code}`);
  return def;
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && byCode.has(value);
}

/** User-facing message always carries the identifier, never a bare "Something went wrong". */
export function formatUserMessage(code: ErrorCode): string {
  return `${getErrorDefinition(code).userMessage} Error ${code}.`;
}

/** An error that carries a registered code across a module boundary. */
export class CodedError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly context: Readonly<Record<string, unknown>>;
  /** Field-level problems safe to return to the client (paths and rule names, never values). */
  readonly issues: readonly { path: string; problem: string }[] | undefined;

  constructor(
    code: ErrorCode,
    message: string,
    options: {
      httpStatus?: number;
      context?: Record<string, unknown>;
      cause?: unknown;
      issues?: { path: string; problem: string }[];
    } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'CodedError';
    this.code = code;
    this.httpStatus = options.httpStatus ?? 500;
    this.context = options.context ?? {};
    this.issues = options.issues;
  }
}
